import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { setActiveCurrency, SUPPORTED_CURRENCIES } from '@/lib/format';

const CurrencyContext = createContext(null);

const LS_KEY = 'recoup_currency';

const initialCurrency = () => {
  const stored = localStorage.getItem(LS_KEY);
  return stored && SUPPORTED_CURRENCIES.includes(stored) ? stored : 'USD';
};

export const CurrencyProvider = ({ children }) => {
  const { user, setUser } = useAuth();
  const [currency, setCurrencyState] = useState(initialCurrency);
  const [rates, setRates] = useState(null);
  const [ready, setReady] = useState(false);

  // Fetch live FX rates once (public endpoint). Sync into format.js synchronously.
  useEffect(() => {
    let cancelled = false;
    api.get('/fx/rates')
      .then((r) => {
        if (cancelled) return;
        const map = r.data?.rates || null;
        if (map) { setActiveCurrency(currency, map); setRates(map); }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setReady(true); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the authenticated user carries a saved preference, adopt it.
  useEffect(() => {
    const pref = user?.currency;
    if (pref && SUPPORTED_CURRENCIES.includes(pref) && pref !== currency) {
      setActiveCurrency(pref, null);
      setCurrencyState(pref);
      localStorage.setItem(LS_KEY, pref);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.currency]);

  const setCurrency = useCallback((code) => {
    if (!SUPPORTED_CURRENCIES.includes(code)) return;
    // Update module-level state SYNCHRONOUSLY so re-renders format correctly.
    setActiveCurrency(code, null);
    setCurrencyState(code);
    localStorage.setItem(LS_KEY, code);
    // Persist to backend + keep AuthContext user in sync (best-effort).
    if (user) {
      api.patch('/auth/me/preferences', { currency: code })
        .then((r) => {
          setUser?.(r.data);
          localStorage.setItem('cocc_user', JSON.stringify(r.data));
        })
        .catch(() => {});
    }
  }, [user, setUser]);

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, rates, ready }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) return { currency: 'USD', setCurrency: () => {}, rates: null, ready: false };
  return ctx;
};
